import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './ProductCombo.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ProductCombo = () => {
  const { user, pageAccess } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const product_id = location.state?.product_id;
  const freeze_definition = location.state?.freeze_definition || 'N';
  const searchForm = location.state?.searchForm;
  const isFrozen = freeze_definition === 'Y';
  const sub = localStorage.getItem('selectedSubsidiaryKey') || 'LH';
  const auth = () => ({ headers: { Authorization: `Bearer ${user?.token}` } });

  const [combos, setCombos] = useState([]);
  const [childLov, setChildLov] = useState([]);
  const [productName, setProductName] = useState('');
  const [productCost, setProductCost] = useState(null);
  const [canEditFrozen, setCanEditFrozen] = useState(false);
  const [modal, setModal] = useState(false);
  const [childId, setChildId] = useState('');
  const [childInput, setChildInput] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const [quantity, setQuantity] = useState('');
  const [portions, setPortions] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    (async () => {
      if (user && pageAccess) {
        const ok = await pageAccess('freezeProductDefinition', user.role);
        setCanEditFrozen(ok);
      }
      await Promise.all([fetchCombos(), fetchChildLov(), fetchProductInfo(), computeCost()]);
    })();
  }, [product_id]);

  useEffect(() => {
    const handler = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setShowDrop(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchCombos = async () => {
    try {
      const res = await axios.get(`${API_URL}/lhProductCombo/byProdId/${product_id}`, auth());
      setCombos(res.data);
    } catch {}
  };

  const fetchChildLov = async () => {
    try {
      const res = await axios.get(`${API_URL}/commonLOVs/lhproducts/${sub}?prod_or_combo=P`, auth());
      setChildLov(res.data);
    } catch {}
  };

  const fetchProductInfo = async () => {
    try {
      const res = await axios.get(`${API_URL}/products/byid/${product_id}`, auth());
      setProductName(res.data?.name || '');
    } catch {}
  };

  const computeCost = async () => {
    try {
      const combosRes = await axios.get(`${API_URL}/lhProductCombo/byProdId/${product_id}`, auth());
      let total = 0;
      for (const c of combosRes.data) {
        const costRes = await axios.get(`${API_URL}/products/productcost/${c.child_product_id}`, auth());
        const childCost = parseFloat(costRes.data?.product_cost || 0);
        total += (childCost * parseFloat(c.quantity || 0)) / parseFloat(c.no_of_portions || 1);
      }
      setProductCost(total.toFixed(2));
    } catch {}
  };

  const handleChildInput = (e) => {
    const val = e.target.value;
    setChildInput(val);
    if (!val.trim()) {
      setFiltered([]);
      setShowDrop(false);
      setChildId('');
      return;
    }
    const f = childLov.filter(i => i.name.toLowerCase().includes(val.toLowerCase()));
    setFiltered(f.slice(0, 12));
    setShowDrop(f.length > 0);
  };

  const selectChild = (item) => {
    setChildInput(item.name);
    setChildId(item.id);
    setShowDrop(false);
    setFiltered([]);
  };

  const openModal = () => {
    setChildId('');
    setChildInput('');
    setQuantity('');
    setPortions('');
    setError('');
    setModal(true);
  };

  const closeModal = () => { setModal(false); setError(''); };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!childId) { setError('Please select a child product.'); return; }
    if (!quantity) { setError('Quantity is required.'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/lhProductCombo`, {
        product_id,
        child_product_id: childId,
        quantity,
        no_of_portions: portions,
      }, auth());
      closeModal();
      fetchCombos();
      computeCost();
    } catch {
      setError('Failed to add combo entry.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (child_product_id) => {
    if (!window.confirm('Remove this child product from the combo?')) return;
    try {
      await axios.delete(`${API_URL}/lhProductCombo/${product_id}/${child_product_id}`, auth());
      fetchCombos();
      computeCost();
    } catch {}
  };

  return (
    <div className="pc-page">

      {/* Back */}
      <button className="pc-back-btn" onClick={() => navigate('/setup/products', { state: { searchForm } })}>
        ← Back to Products
      </button>

      {/* Info bar */}
      <div className="pc-info-bar">
        <div className="pc-info-name">{productName || `Product #${product_id}`}</div>
        <div className="pc-info-cost">
          Cost: <strong>{productCost != null ? `₹${parseFloat(productCost).toFixed(2)}` : '—'}</strong>
        </div>
        <button className="pc-refresh-btn" onClick={() => computeCost()} title="Refresh cost">↺ Refresh</button>
        <button className="btn-add-user" onClick={openModal} disabled={isFrozen && !canEditFrozen}>
          <span className="icon">+</span> Add Child Product
        </button>
      </div>

      {/* Table */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Child Product</th>
              <th>Quantity</th>
              <th>No of Portions</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {combos.length === 0 ? (
              <tr><td colSpan={4} className="no-data">No combo entries defined.</td></tr>
            ) : combos.map(c => (
              <tr key={c.child_product_id}>
                <td>{c.product_name}</td>
                <td>{c.quantity}</td>
                <td>{c.no_of_portions}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-delete"
                      onClick={() => handleDelete(c.child_product_id)}
                      disabled={isFrozen && !canEditFrozen}
                      title="Remove"
                    >🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Modal */}
      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-content--wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Child Product</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleAdd}>
              {error && <div className="pc-form-error">{error}</div>}
              <div className="pc-form-body">

                {/* Child product autocomplete */}
                <div className="form-group">
                  <label>Child Product *</label>
                  <div className="pc-ac-wrap" ref={dropRef}>
                    <input
                      className="form-input"
                      value={childInput}
                      onChange={handleChildInput}
                      onFocus={() => childInput && filtered.length > 0 && setShowDrop(true)}
                      placeholder="Type product name…"
                      autoComplete="off"
                    />
                    {showDrop && (
                      <ul className="pc-ac-dropdown">
                        {filtered.map(i => (
                          <li key={i.id} className="pc-ac-option" onMouseDown={() => selectChild(i)}>
                            {i.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="pc-form-row2">
                  <div className="form-group">
                    <label>Quantity *</label>
                    <input required type="number" step="any" min="0" className="form-input"
                      value={quantity} onChange={e => setQuantity(e.target.value)} placeholder="Quantity" />
                  </div>
                  <div className="form-group">
                    <label>No of Portions</label>
                    <input type="number" step="any" min="1" className="form-input"
                      value={portions} onChange={e => setPortions(e.target.value)} placeholder="No of Portions" />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-submit" disabled={saving}>
                  {saving ? 'Adding…' : 'Add Child Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCombo;
